import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, CheckCheck, Sparkles, ImageIcon } from "lucide-react";
import { AcornPlaceholder, absMediaUrl } from "@/components/AcornPlaceholder";

const STATUS_LABEL = {
    da_arricchire: "Da Arricchire",
    arricchito_in_validazione: "In Validazione",
    validato_sommelier: "Validato",
};
const VISIBILITY_LABEL = {
    auto: "Auto",
    force_visible: "Visibile",
    force_hidden: "Nascosto",
};
import VisibilityBreakdownPopover from "./VisibilityBreakdownPopover";

const CATEGORY_LABEL = {
    vino: "Solo Vini",
    non_vino: "Non-Vini (tutti)",
    distillato: "Distillati",
    liquore: "Liquori",
    birra: "Birre",
    altro: "Altro",
};
// Fase 8.5: default della lista /admin/vini è filtrato su categoria=vino
// (i non-vino hanno la loro sezione dedicata /admin/distillati). L'admin
// può passare a "Tutti" per un catalogo unificato.
const DEFAULT_CATEGORY = "vino";
const QUALITY_LABEL = {
    no_producer: "Senza produttore",
    no_price: "Senza prezzo",
    hidden_from_public: "Nascosti al pubblico",
};

export default function AdminWines() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [publicCount, setPublicCount] = useState(0);
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [visibilityFilter, setVisibilityFilter] = useState("");
    // Fase 8.5: default categoria=vino (i distillati sono in /admin/distillati)
    const [categoriaFilter, setCategoriaFilter] = useState(DEFAULT_CATEGORY);
    const [qualityFilter, setQualityFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [validating, setValidating] = useState(false);
    const [enriching, setEnriching] = useState(null);
    const [imgSearching, setImgSearching] = useState(null);
    const [inferStatus, setInferStatus] = useState(null);
    const [inferStarting, setInferStarting] = useState(false);

    useEffect(() => {
        const h = setTimeout(() => setDebouncedQ(q.trim()), 300);
        return () => clearTimeout(h);
    }, [q]);

    const loadInferStatus = () => {
        api.get("/admin/ai/infer-producers/status")
            .then(({ data }) => setInferStatus(data))
            .catch(() => setInferStatus(null));
    };

    const load = () => {
        setLoading(true);
        const params = { limit: 200 };
        if (debouncedQ) params.q = debouncedQ;
        if (statusFilter) params.status = statusFilter;
        if (visibilityFilter) params.visibility = visibilityFilter;
        if (categoriaFilter) params.categoria = categoriaFilter;
        if (qualityFilter) params.filter = qualityFilter;
        if (sourceFilter) params.source = sourceFilter;
        api.get("/admin/wines", { params })
            .then(({ data }) => {
                setItems(data.items);
                setTotal(data.total);
                setPublicCount(data.public_count || 0);
                setSelected(new Set());
            })
            .catch(() => toast.error("Errore nel caricamento"))
            .finally(() => setLoading(false));
    };
    useEffect(load, [debouncedQ, statusFilter, visibilityFilter, categoriaFilter, qualityFilter, sourceFilter]);

    // Refresh producer-inference status on mount + every 15s while active
    useEffect(() => {
        loadInferStatus();
        const t = setInterval(loadInferStatus, 15000);
        return () => clearInterval(t);
    }, []);

    const startProducerInference = async () => {
        setInferStarting(true);
        try {
            const { data } = await api.post("/admin/ai/infer-producers/start");
            toast.success(`Inferenza produttori avviata: ${data.newly_enqueued} nuovi in coda (${data.pending_now} totali pending)`);
            loadInferStatus();
        } catch (e) {
            toast.error("Errore nell'avvio del worker");
        } finally {
            setInferStarting(false);
        }
    };

    const toggleSel = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((w) => w.id)));
    };

    const bulkValidate = async () => {
        if (selected.size === 0) return;
        setValidating(true);
        try {
            const { data } = await api.post("/admin/ai/validate-wines", {
                wine_ids: Array.from(selected),
            });
            toast.success(`${data.validated} vini validati`);
            load();
        } catch { toast.error("Errore validazione"); } finally { setValidating(false); }
    };

    const doDelete = async (id) => {
        try {
            await api.delete(`/admin/wines/${id}`);
            toast.success("Vino eliminato");
            load();
        } catch { toast.error("Eliminazione fallita"); } finally { setDeleting(null); }
    };

    const enrichOne = async (id) => {
        setEnriching(id);
        try {
            await api.post(`/admin/ai/enrich-wine/${id}`);
            toast.success("Scheda arricchita — controlla e valida");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore AI"); } finally { setEnriching(null); }
    };

    const searchImage = async (id) => {
        setImgSearching(id);
        try {
            await api.post(`/admin/wines/${id}/image/search`);
            toast.success("Immagine trovata");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ricerca fallita — carica manualmente");
        } finally { setImgSearching(null); }
    };

    return (
        <div data-testid="admin-wines-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl">Gestione Vini</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-3 flex-wrap" data-testid="admin-wines-header-count">
                        <span>
                            {total} vin{total === 1 ? "o" : "i"}
                            <span className="mx-2 text-[hsl(var(--border))]">·</span>
                            <span className="text-[hsl(var(--primary))]">{publicCount}</span> pubblic{publicCount === 1 ? "o" : "i"}
                            {selected.size > 0 && (
                                <>
                                    <span className="mx-2 text-[hsl(var(--border))]">·</span>
                                    {selected.size} selezionati
                                </>
                            )}
                        </span>
                        <VisibilityBreakdownPopover
                            categoria={categoriaFilter || "vino"}
                            onJumpToFilter={(k) => {
                                setQualityFilter(k);
                            }}
                        />
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        className="co-btn-ghost"
                        disabled={selected.size === 0 || validating}
                        onClick={bulkValidate}
                        data-testid="admin-wines-bulk-validate-btn"
                    >
                        <CheckCheck className="w-4 h-4" />
                        {validating ? "Validazione…" : `Valida (${selected.size})`}
                    </button>
                    <button
                        className="co-btn-primary"
                        onClick={() => navigate("/admin/vini/nuovo")}
                        data-testid="admin-wines-new-btn"
                    >
                        <Plus className="w-4 h-4" /> Nuovo Vino
                    </button>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                    <Input
                        placeholder="Cerca nome, produttore, denominazione, codice art., uvaggio…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        data-testid="admin-wines-search"
                        className="pl-10 bg-white"
                    />
                </div>
                <Select value={statusFilter || "__all"} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="admin-wines-status-filter">
                        <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutti gli stati</SelectItem>
                        {Object.entries(STATUS_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={visibilityFilter || "__all"} onValueChange={(v) => setVisibilityFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="admin-wines-visibility-filter">
                        <SelectValue placeholder="Visibilità" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutte visibilità</SelectItem>
                        {Object.entries(VISIBILITY_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={categoriaFilter || "__all"} onValueChange={(v) => setCategoriaFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="admin-wines-categoria-filter">
                        <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutte categorie</SelectItem>
                        {Object.entries(CATEGORY_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={qualityFilter || "__all"} onValueChange={(v) => setQualityFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="admin-wines-quality-filter">
                        <SelectValue placeholder="Qualità dati" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutti i vini</SelectItem>
                        {Object.entries(QUALITY_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={sourceFilter || "__all"} onValueChange={(v) => setSourceFilter(v === "__all" ? "" : v)}>
                    <SelectTrigger
                        className="w-full sm:w-40 bg-white"
                        data-testid="admin-wines-source-filter"
                        title="Sorgente del vino: Mexal (dal gestionale, giacenze live) · File (import CSV/PDF) · Foto (creato dalla foto della bottiglia, giacenza null, fuori dal sync Mexal, sempre visibile) · Manuale (inserito a mano)"
                    >
                        <SelectValue placeholder="Sorgente" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Tutte sorgenti</SelectItem>
                        <SelectItem value="mexal">Mexal</SelectItem>
                        <SelectItem value="file">File (CSV/PDF)</SelectItem>
                        <SelectItem value="photo" title="Vini creati dalla foto della bottiglia: giacenza null, sempre visibili finché non nascosti, non toccati dal sync Mexal.">Foto</SelectItem>
                        <SelectItem value="manual">Manuale</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {categoriaFilter !== DEFAULT_CATEGORY && (
                <div className="mb-4 p-3 bg-[#FDFBF7] border border-[#D9B98F] rounded-sm text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="admin-wines-mixed-banner">
                    <span className="text-[hsl(var(--foreground))]">
                        {categoriaFilter === ""
                            ? <>Stai vedendo <b>tutti i prodotti</b> (vini + distillati/liquori/birre). I non-vino hanno una sezione dedicata.</>
                            : <>Filtro corrente: <b>{CATEGORY_LABEL[categoriaFilter] || categoriaFilter}</b>. I non-vino hanno una sezione dedicata.</>}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setCategoriaFilter(DEFAULT_CATEGORY)}
                            className="co-btn-ghost text-xs"
                            data-testid="admin-wines-back-to-wines"
                        >
                            Torna a Solo Vini
                        </button>
                        <Link
                            to="/admin/distillati"
                            className="co-btn-ghost text-xs"
                            data-testid="admin-wines-goto-dist"
                        >
                            Apri Distillati
                        </Link>
                    </div>
                </div>
            )}

            {/* Producer inference (Fase 6.3): live status + start button */}
            {inferStatus && (inferStatus.wines_without_producer > 0 || inferStatus.pending > 0 || inferStatus.done_resolved > 0) && (
                <div
                    className="mb-4 p-4 bg-white border border-[hsl(var(--border))] rounded-sm"
                    data-testid="admin-wines-infer-producer-card"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                            <div className="co-caption mb-1">Inferenza produttori (AI, seconda passata)</div>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                                <span className="font-medium text-[hsl(var(--foreground))]">
                                    {inferStatus.wines_without_producer}
                                </span> vini normalizzati senza produttore ·
                                <span className="mx-1 text-[hsl(var(--primary))]">
                                    {inferStatus.done_resolved}
                                </span> risolti ·
                                <span className="mx-1">
                                    {inferStatus.done_unresolved}
                                </span> da rivedere manualmente ·
                                <span className="mx-1">
                                    {inferStatus.pending}
                                </span> in coda
                                {inferStatus.errors > 0 && (
                                    <span className="ml-1 text-[hsl(var(--destructive))]">
                                        · {inferStatus.errors} errori
                                    </span>
                                )}
                            </p>
                        </div>
                        <button
                            className="co-btn-ghost text-sm"
                            onClick={startProducerInference}
                            disabled={inferStarting}
                            data-testid="admin-wines-start-infer-producer-btn"
                        >
                            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                            {inferStarting ? "Avvio…" : (inferStatus.pending > 0 ? "Riavvia" : "Avvia")}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={selected.size > 0 && selected.size === items.length}
                                    onCheckedChange={toggleAll}
                                    data-testid="admin-wines-toggle-all"
                                />
                            </TableHead>
                            <TableHead>Immagine</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Produttore</TableHead>
                            <TableHead>Annata</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead>Visibilità</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={8} className="text-center py-8 text-[hsl(var(--muted-foreground))]">Caricamento…</TableCell></TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={8} className="text-center py-8 text-[hsl(var(--muted-foreground))]">Nessun vino</TableCell></TableRow>
                        )}
                        {items.map((w) => {
                            const thumb = absMediaUrl(w.images?.thumb_url);
                            return (
                                <TableRow key={w.id} data-testid={`admin-wine-row-${w.id}`}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selected.has(w.id)}
                                            onCheckedChange={() => toggleSel(w.id)}
                                            data-testid={`admin-wine-select-${w.id}`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="w-12 h-12 flex items-center justify-center bg-[hsl(var(--secondary))] rounded-sm">
                                            {thumb ? (
                                                <img src={thumb} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                                            ) : (
                                                <AcornPlaceholder size={30} opacity={0.55} />
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="text-base">{w.name} {w.featured ? <span className="ml-1 text-[hsl(var(--accent))]" title="In evidenza">★</span> : null}</div>
                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                            {w.region} · {w.type}
                                            {(w.formats || []).some((f) => f.mexal_sku) && (
                                                <span className="ml-2 font-mono" data-testid={`admin-wine-sku-${w.id}`}>
                                                    · {(w.formats || []).filter((f) => f.mexal_sku).map((f) => f.mexal_sku).join(" / ")}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm">{w.producer}</TableCell>
                                    <TableCell>{w.vintage || "—"}</TableCell>
                                    <TableCell>
                                        <span className="co-badge-neutral">{STATUS_LABEL[w.status]}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="co-badge-neutral">{VISIBILITY_LABEL[w.admin_visible_override]}</span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1 flex-wrap">
                                            <button
                                                type="button"
                                                onClick={() => enrichOne(w.id)}
                                                disabled={enriching === w.id}
                                                className="co-btn-ghost text-xs px-2"
                                                title="Arricchisci con AI"
                                                data-testid={`admin-wine-enrich-${w.id}`}
                                            >
                                                <Sparkles className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => searchImage(w.id)}
                                                disabled={imgSearching === w.id}
                                                className="co-btn-ghost text-xs px-2"
                                                title="Cerca immagine"
                                                data-testid={`admin-wine-image-search-${w.id}`}
                                            >
                                                <ImageIcon className="w-3.5 h-3.5" />
                                            </button>
                                            <Link
                                                to={`/admin/vini/${w.id}`}
                                                className="co-btn-ghost text-xs px-2"
                                                data-testid={`admin-wine-edit-${w.id}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Link>
                                            <AlertDialog open={deleting === w.id} onOpenChange={(v) => !v && setDeleting(null)}>
                                                <AlertDialogTrigger asChild>
                                                    <button
                                                        type="button"
                                                        className="co-btn-ghost text-xs px-2 text-[hsl(var(--destructive))]"
                                                        onClick={() => setDeleting(w.id)}
                                                        data-testid={`admin-wine-delete-${w.id}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Eliminare "{w.name}"?</AlertDialogTitle>
                                                        <AlertDialogDescription>Azione irreversibile.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => doDelete(w.id)}
                                                            data-testid={`admin-wine-delete-confirm-${w.id}`}
                                                            className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
                                                        >
                                                            Elimina
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
